import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, chapters, reviews } from '@/mocks/comics';
import { siteConfig } from '@/mocks/site';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.id === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="container-custom text-center py-20">
            <p className="text-foreground-600 mb-4">漫画未找到</p>
            <Link
              to="/category/all"
              className="inline-block px-6 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              浏览全部漫画
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags?.some((t) => comic.tags?.includes(t))))
    .slice(0, 6);

  const comicReviews = reviews.slice(0, 3);

  return (
    <>
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="container-custom">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.category === '热血' ? 'hot-blood' : comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '剧情' ? 'drama' : comic.category === '惊悚' ? 'thriller' : comic.category === '喜剧' ? 'comedy' : 'all'}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="py-6 md:py-10">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="lg:w-[40%] flex flex-col">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1">
                  {comic.title}
                </h1>
                <p className="text-base md:text-lg text-foreground-600 font-heading mb-6">
                  {comic.titleEn}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/all`}
                      className="px-3 py-1 text-xs rounded-full border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-8">
                  {comic.description}
                </p>

                <Link
                  to={`/comic/${comic.id}/read`}
                  className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-full bg-background-950 text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读 <i className="ri-arrow-right-line"></i>
                </Link>

                <div className="flex items-center gap-8 mt-8 pt-6 border-t border-background-200/70">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground-950 font-heading">{comic.chapters}</p>
                    <p className="text-xs text-foreground-500">话</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground-950 font-heading">{comic.rating}</p>
                    <p className="text-xs text-foreground-500">评分</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-foreground-950 font-heading">{comic.views}</p>
                    <p className="text-xs text-foreground-500">阅读量</p>
                  </div>
                </div>
              </div>

              <div className="lg:w-[60%] relative">
                <div className="flex items-start gap-4">
                  <div className="w-32 md:w-40 flex-shrink-0 rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.titleEn}`}
                      className="w-full aspect-[3/4] object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} 封面大图`}
                      title={`${comic.title} - 韩漫免费看 下拉式全览`}
                      className="w-full aspect-[3/4] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-100/50">
          <div className="container-custom">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {chapters.map((ch) => (
                <Link
                  key={ch.number}
                  to={`/comic/${comic.id}/read`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors group cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                      第{ch.number}话 {ch.title}
                    </p>
                    <p className="text-xs text-foreground-500">{ch.date}</p>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-3">{ch.views}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="container-custom">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
              读者评价
            </h2>
            <div className="space-y-4">
              {comicReviews.map((review) => (
                <article
                  key={review.id}
                  className="bg-background-950 rounded-2xl p-6 md:p-8"
                  itemProp="review"
                  itemScope
                  itemType="https://schema.org/Review"
                >
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden flex-shrink-0 -ml-10 bg-background-100">
                      <img
                        src={review.avatar}
                        alt={review.user}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-accent-400 text-4xl md:text-5xl font-heading leading-none mb-2">
                        &ldquo;
                      </div>
                      <p className="text-sm md:text-base text-background-50 leading-relaxed mb-4" itemProp="reviewBody">
                        {review.content}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-background-50" itemProp="author">
                            {review.user}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <span key={i} className="text-xs text-accent-400">★</span>
                            ))}
                          </div>
                        </div>
                        <button className="px-4 py-1.5 text-xs font-medium rounded-full bg-accent-400/20 text-accent-300 hover:bg-accent-400/30 transition-colors cursor-pointer whitespace-nowrap">
                          有用
                        </button>
                      </div>
                    </div>
                  </div>
                  <meta itemProp="datePublished" content={review.date} />
                  <span
                    itemProp="reviewRating"
                    itemScope
                    itemType="https://schema.org/Rating"
                  >
                    <meta itemProp="ratingValue" content={String(review.rating)} />
                    <meta itemProp="bestRating" content="5" />
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="container-custom">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedComics.map((item) => (
                <ComicCard
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  author={item.author}
                  category={item.category}
                  rating={item.rating}
                  views={item.views}
                  chapters={item.chapters}
                  status={item.status}
                  coverImage={item.coverImage}
                  tags={item.tags}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-950">
          <div className="container-custom">
            <div className="flex flex-col items-center text-center max-w-xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-background-50 font-heading mb-3">
                订阅更新提醒
              </h2>
              <p className="text-sm text-foreground-400 mb-8">
                新章节第一时间通知您，不错过任何精彩内容
              </p>
              <Link
                to="/about#contact"
                className="inline-flex items-center gap-2 px-8 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                订阅更新 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${comic.title} - ${comic.titleEn}`,
              description: comic.description,
              author: {
                '@type': 'Person',
                name: comic.author,
              },
              datePublished: comic.updateDate,
              image: comic.coverImage,
              publisher: {
                '@type': 'Organization',
                name: siteConfig.name,
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${siteConfig.url}/comic/${comic.id}`,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: siteConfig.url,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: `${comic.category}漫画`,
                  item: `${siteConfig.url}/category/${comic.category === '热血' ? 'hot-blood' : 'romance'}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: comic.title,
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}